import type { ReactNode } from "react";
import { PolicyDocument, type TermsData } from "@dwelldrop/shared";
import { PolicyMarkdown } from "../../ui/PolicyMarkdown";

const renderPolicyMarkdown = (markdown: string): ReactNode => (
  <PolicyMarkdown>{markdown}</PolicyMarkdown>
);

type TermsModalProps = {
  data: TermsData;
  version?: number;
  whatsNew?: string;
  onClose: () => void;
  showAcceptButton?: boolean;
  onAccept?: () => void;
  /** Override backdrop (e.g. z-index when nested inside another modal). */
  backdropClassName?: string;
};

export default function TermsModal({
  data,
  version,
  whatsNew,
  onClose,
  showAcceptButton = false,
  onAccept,
  backdropClassName = "fixed inset-0 z-[100] flex items-center justify-center bg-black bg-opacity-50 p-4",
}: TermsModalProps) {
  return (
    <div
      className={backdropClassName}
      data-testid="terms-modal"
      onClick={(e) => {
        e.stopPropagation();
        onClose();
      }}
    >
      <div
        className="max-h-[90vh] w-full max-w-3xl overflow-y-auto rounded-2xl bg-white shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Gradient header */}
        <div className="sticky top-0 z-10 rounded-t-2xl bg-gradient-to-r from-blue-500 to-blue-600 p-6 text-white">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-white/20">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="lucide lucide-file-text h-6 w-6"
                >
                  <path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z"></path>
                  <polyline points="14 2 14 8 20 8"></polyline>
                  <line x1="16" x2="8" y1="13" y2="13"></line>
                  <line x1="16" x2="8" y1="17" y2="17"></line>
                  <line x1="10" x2="8" y1="9" y2="9"></line>
                </svg>
              </div>
              <div>
                <h2 className="text-center text-2xl font-bold">
                  Standard Merchant Terms
                </h2>
              </div>
            </div>
            <button
              className="text-2xl font-light text-white/80 hover:text-white"
              onClick={(e) => {
                e.stopPropagation();
                onClose();
              }}
            >
              ×
            </button>
          </div>
        </div>

        <div className="space-y-4 p-6">
          <PolicyDocument
            data={data}
            policyName="terms_and_condition_venue"
            version={version ?? data.version}
            whatsNew={whatsNew}
            renderMarkdown={renderPolicyMarkdown}
          />

          {/* Action Button */}
          {showAcceptButton && onAccept ? (
            <button
              onClick={onAccept}
              className="w-full rounded-lg bg-gradient-to-r from-blue-500 to-blue-600 px-4 py-3 text-base font-semibold text-white transition-all duration-200 hover:scale-105 hover:shadow-lg"
            >
              I Accept These Terms
            </button>
          ) : (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onClose();
              }}
              className="w-full rounded-lg bg-gradient-to-r from-blue-500 to-blue-600 px-4 py-3 text-base font-semibold text-white transition-all duration-200 hover:scale-105 hover:shadow-lg"
            >
              Close
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
